
import { MachineComponentModel, MachineComponentWorkplaceModel, QueryOperator } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Ref } from 'vue-property-decorator';
import { machineComponentsStore } from '@/libs/machine-components/+state/store';
import { machineComponentWorkplacesStore } from '@/libs/machine-components/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import MachineComponentSelectDialog from './MachineComponentSelectDialog.vue';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';

@Component({
    components: { MachineComponentSelectDialog },
})
export default class WorkplaceMachineComponentsList extends Page {
    @Prop() workplaceId!: string;
    @Prop({ default: false }) compact!: boolean;
    @Ref() selectDialog!: any;

    loading = false;

    mounted() {
        this.loadData();
    }

    loadData() {
        this.loading = true;
        machineComponentWorkplacesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(undefined, [{ field: 'workplaceId', op: QueryOperator.Eq, comparand: this.workplaceId }])
        );
        this.subscribe(machineComponentWorkplacesStore, CrudReponse.GetAll)
            .then(() => {
                this.loading = false;
            })
            .catch(() => {
                this.loading = false;
            });
    }

    get componentWorkplaces(): MachineComponentWorkplaceModel[] {
        const items = (machineComponentWorkplacesStore.useGetter(CrudGetter.Data) as MachineComponentWorkplaceModel[]) ?? [];
        return items.filter((x) => x.workplaceId === this.workplaceId);
    }

    get sortedComponentWorkplaces(): MachineComponentWorkplaceModel[] {
        return [...this.componentWorkplaces].sort((a, b) => {
            const componentA = this.getComponent(a.machineComponentId);
            const componentB = this.getComponent(b.machineComponentId);
            return (componentA?.code ?? '').localeCompare(componentB?.code ?? '');
        });
    }

    get allComponents(): MachineComponentModel[] {
        return (machineComponentsStore.useGetter(CrudGetter.Data) as MachineComponentModel[]) ?? [];
    }

    getComponent(componentId: string | undefined): MachineComponentModel | undefined {
        if (!componentId) return undefined;
        return this.allComponents.find((x) => x.id === componentId);
    }

    get currentComponentIds(): string[] {
        return this.componentWorkplaces.map(cw => cw.machineComponentId!);
    }

    openSelectDialog() {
        this.selectDialog.open(this.currentComponentIds);
    }

    onComponentsSelected(selectedComponents: MachineComponentModel[]) {
        const selectedIds = selectedComponents.map(c => c.id!);
        const currentIds = this.currentComponentIds;

        // Najít nově přidané
        const toAdd = selectedComponents.filter(c => !currentIds.includes(c.id!));
        
        // Najít odebrané
        const toRemove = this.componentWorkplaces.filter(cw => !selectedIds.includes(cw.machineComponentId!));

        const promises: Promise<any>[] = [];

        // Přidat nové komponenty
        toAdd.forEach((component) => {
            const componentWorkplace: MachineComponentWorkplaceModel = {
                id: Guid.EmptyGuid(),
                machineComponentId: component.id,
                workplaceId: this.workplaceId,
            };
            const promise = new Promise((resolve, reject) => {
                machineComponentWorkplacesStore.dispatch(CrudAction.Create, { item: componentWorkplace });
                this.subscribe(machineComponentWorkplacesStore, CrudReponse.Create)
                    .then(resolve)
                    .catch(reject);
            });
            promises.push(promise);
        });

        // Odebrat komponenty
        toRemove.forEach((componentWorkplace) => {
            const promise = new Promise((resolve, reject) => {
                machineComponentWorkplacesStore.dispatch(CrudAction.Delete, { id: componentWorkplace.id });
                this.subscribe(machineComponentWorkplacesStore, CrudReponse.Delete)
                    .then(resolve)
                    .catch(reject);
            });
            promises.push(promise);
        });

        Promise.all(promises).then(() => {
            this.loadData();
        });
    }
}
